import React, { useState, useEffect, useRef } from 'react';
import { downloadsAPI, clientsAPI } from '../../services/api';
import DownloadModal from './DownloadModal';
import Swal from 'sweetalert2';
import {
  Download,
  Plus,
  Filter,
  RotateCcw,
  Users,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Package,
  X,
  FileText,
  TrendingUp,
  Loader2,
  RefreshCw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  AlertTriangle,
  BarChart3,
} from 'lucide-react';

const DownloadManagement = () => {
  const [downloads, setDownloads] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [clientsError, setClientsError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para filtros y paginación
  const [filters, setFilters] = useState({
    clientId: '',
    status: '',
    fechaDesde: '',
    fechaHasta: ''
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false
  });

  const [sorting, setSorting] = useState({
    orderBy: 'CreatedAt',
    orderDirection: 'desc'
  });

  const [showFilters, setShowFilters] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Estado y ref para el efecto de scroll - MEJORADO
  const [showStatsInfo, setShowStatsInfo] = useState(true);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef(null);
  const isScrolling = useRef(false);
  const scrollThreshold = 100;

  useEffect(() => {
    loadInitialData();
  }, []);

  // Cargar downloads cuando cambian filtros, paginación o sorting
  useEffect(() => {
    if (!loading) {
      loadDownloads();
    }
  }, [filters, pagination.currentPage, sorting]);

  // Auto-refresh cada 1 minuto
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading) {
        console.log('🔄 Actualizando descargas automáticamente cada minuto...');
        loadDownloads(true);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [filters, pagination.currentPage, sorting]);

  // Efecto de scroll MEJORADO
  useEffect(() => {
    const handleScroll = () => {
      if (isScrolling.current) return;

      const currentScrollY = window.scrollY;

      if (Math.abs(currentScrollY - lastScrollY.current) < scrollThreshold) {
        return;
      }

      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      scrollTimeout.current = setTimeout(() => {
        if (currentScrollY > lastScrollY.current && currentScrollY > 200) {
          if (showStatsInfo) {
            isScrolling.current = true;
            setShowStatsInfo(false);
            setTimeout(() => {
              isScrolling.current = false;
            }, 400);
          }
        } else if (currentScrollY < lastScrollY.current || currentScrollY < 100) {
          if (!showStatsInfo) {
            isScrolling.current = true;
            setShowStatsInfo(true);
            setTimeout(() => {
              isScrolling.current = false;
            }, 400);
          }
        }

        lastScrollY.current = currentScrollY;
      }, 50);
    };

    let ticking = false;
    const scrollListener = () => {
      if (!ticking && !isScrolling.current) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', scrollListener, { passive: true });
    return () => {
      window.removeEventListener('scroll', scrollListener);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [showStatsInfo]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setClientsLoading(true);

      const [downloadsRes, clientsRes] = await Promise.allSettled([
        loadDownloads(true),
        clientsAPI.getAll()
      ]);

      if (clientsRes.status === 'fulfilled') {
        const clientsData = Array.isArray(clientsRes.value.data) ? clientsRes.value.data : [];
        setClients(clientsData);
        setClientsError('');
      } else {
        setClientsError('Error al cargar clientes: ' + (clientsRes.reason.response?.data?.message || clientsRes.reason.message));
        setClients([]);
      }

    } finally {
      setLoading(false);
      setClientsLoading(false);
    }
  };

  const loadDownloads = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      const params = {
        ...filters,
        page: pagination.currentPage,
        pageSize: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      console.log('Cargando downloads con parámetros:', params);

      const response = await downloadsAPI.getAll(params);
      const data = response.data;

      setDownloads(data.items || []);
      setPagination({
        currentPage: data.currentPage || 1,
        pageSize: data.pageSize || 20,
        totalItems: data.totalItems || 0,
        totalPages: data.totalPages || 0,
        hasPreviousPage: data.hasPreviousPage || false,
        hasNextPage: data.hasNextPage || false
      });

      setLastUpdate(new Date());

    } catch (error) {
      console.error('Error cargando descargas:', error);
      if (!silent) {
        setError('Error al cargar descargas: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const reloadClients = async () => {
    try {
      setClientsLoading(true);
      setClientsError('');
      const response = await clientsAPI.getAll();
      const clientsData = Array.isArray(response.data) ? response.data : [];
      setClients(clientsData);
      setSuccess('Clientes recargados exitosamente');
    } catch (error) {
      setClientsError('Error al recargar clientes: ' + (error.response?.data?.message || error.message));
    } finally {
      setClientsLoading(false);
    }
  };

  const handleCreateDownload = () => {
    if (clientsLoading) {
      setError('Esperando a que se carguen los clientes...');
      return;
    }

    if (clients.length === 0) {
      setError('No hay clientes disponibles. Por favor, agrega clientes primero.');
      return;
    }

    setShowModal(true);
  };

  const handleCancelDownload = async (downloadId) => {
    const result = await Swal.fire({
      title: '¿Cancelar Descarga?',
      text: 'Esta acción no se puede deshacer. La descarga en progreso se detendrá.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, mantener'
    });

    if (result.isConfirmed) {
      try {
        await downloadsAPI.cancel(downloadId);
        Swal.fire({
          title: '¡Cancelada!',
          text: 'La descarga ha sido cancelada exitosamente.',
          icon: 'success',
          confirmButtonColor: '#2D3E7F'
        });
        loadDownloads();
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: 'Error al cancelar descarga: ' + (error.response?.data?.message || error.message),
          icon: 'error',
          confirmButtonColor: '#EF4444'
        });
      }
    }
  };

  const handleDownloadPackage = async (downloadId) => {
    try {
      const response = await downloadsAPI.downloadPackage(downloadId);

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `descarga_${downloadId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Descarga iniciada exitosamente');
    } catch (error) {
      setError('Error al descargar archivo: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleModalClose = (shouldReload) => {
    setShowModal(false);
    if (shouldReload) {
      loadDownloads();
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const clearFilters = () => {
    setFilters({
      clientId: '',
      status: '',
      fechaDesde: '',
      fechaHasta: ''
    });
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };

  const handleSortChange = (newOrderBy) => {
    setSorting(prev => ({
      orderBy: newOrderBy,
      orderDirection: prev.orderBy === newOrderBy && prev.orderDirection === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatErrorMessage = (errorMessage) => {
    if (!errorMessage) return '';

    const messages = errorMessage.split(' | ');
    return messages.map((message, index) => {
      const explicitMatch = message.match(/⚠️\s*La descarga de \*\*([^*]+)\*\* falló \(SAT:\s*(\d+)\s*-\s*([^)]+)\)/);

      if (explicitMatch) {
        const tipoDescarga = explicitMatch[1];
        const codigoSAT = explicitMatch[2];
        const descripcionError = explicitMatch[3];
        const isSuccess = codigoSAT === '5000';
        const icon = isSuccess ? '✅' : '❌';
        return `${icon} ${tipoDescarga}: ${descripcionError} (${codigoSAT})`;
      }

      const oldFormatMatch = message.match(/Tipo:\s*([^,]+),?\s*Código SAT:\s*(\d+),?\s*Mensaje:\s*(.+)/);
      if (oldFormatMatch) {
        const tipo = oldFormatMatch[1].trim();
        const codigo = oldFormatMatch[2];
        const mensajeTexto = oldFormatMatch[3].trim();
        const isSuccess = codigo === '5000';
        const icon = isSuccess ? '✅' : '❌';
        return `${icon} ${tipo}: ${mensajeTexto} (${codigo})`;
      }

      return `⚠️ ${message}`;
    }).join(' • ');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { class: 'status-pending', text: 'Pendiente', icon: Clock },
      'SUBMITTED': { class: 'status-pending', text: 'Enviado', icon: TrendingUp },
      'IN_PROGRESS': { class: 'status-processing', text: 'En Progreso', icon: RefreshCw },
      'COMPLETED': { class: 'status-active', text: 'Completado', icon: CheckCircle },
      'FAILED': { class: 'status-inactive', text: 'Fallido', icon: XCircle },
      'ERROR': { class: 'status-inactive', text: 'Error', icon: AlertCircle },
      'Pending': { class: 'status-pending', text: 'Pendiente', icon: Clock },
      'Submitted': { class: 'status-pending', text: 'Enviado', icon: TrendingUp },
      'InProgress': { class: 'status-processing', text: 'En Progreso', icon: RefreshCw },
      'Completed': { class: 'status-active', text: 'Completado', icon: CheckCircle },
      'Failed': { class: 'status-inactive', text: 'Fallido', icon: XCircle },
      'Error': { class: 'status-inactive', text: 'Error', icon: AlertCircle }
    };

    const config = statusConfig[status] || { class: 'status-pending', text: status, icon: Clock };
    const IconComponent = config.icon;

    return (
      <span className={`${config.class} flex items-center space-x-1`}>
        <IconComponent className="w-3 h-3" />
        <span>{config.text}</span>
      </span>
    );
  };

  const getProgressPercentage = (status) => {
    const progressMap = {
      'PENDING': 10, 'SUBMITTED': 25, 'IN_PROGRESS': 75, 'COMPLETED': 100, 'FAILED': 0, 'ERROR': 0,
      'Pending': 10, 'Submitted': 25, 'InProgress': 75, 'Completed': 100, 'Failed': 0, 'Error': 0
    };
    return progressMap[status] || 0;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateRange = (fechaInicio, fechaFin) => {
    const inicio = new Date(fechaInicio).toLocaleDateString('es-ES');
    const fin = new Date(fechaFin).toLocaleDateString('es-ES');
    return `${inicio} - ${fin}`;
  };

  const getClientName = (clientId) => {
    if (!clientId) return 'Sin cliente';
    const client = clients.find(c => c.id === clientId);
    if (!client) return `Cliente ID: ${clientId} (No encontrado)`;
    const rfc = client.rfc || 'Sin RFC';
    const razonSocial = client.razonSocial;
    return razonSocial ? `${rfc} - ${razonSocial}` : rfc;
  };

  const getSortIcon = (column) => {
    if (sorting.orderBy !== column) {
      return <ArrowUpDown className="w-4 h-4 opacity-30" />;
    }
    return sorting.orderDirection === 'asc' ?
      <ArrowUp className="w-4 h-4" /> :
      <ArrowDown className="w-4 h-4" />;
  };

  const hasActiveFilters = filters.clientId || filters.status || filters.fechaDesde || filters.fechaHasta;

  if (loading && pagination.currentPage === 1) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Cargando descargas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div className="flex items-center space-x-3 mb-4 lg:mb-0">
            <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Gestión de Descargas
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Administra y monitorea las descargas masivas del SAT
              </p>
            </div>
          </div>
        </div>

        {/* Panel de Filtros */}
        {showFilters && (
          <div className="card mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-700">
            <div className="flex items-center space-x-2 mb-6">
              <Search className="w-5 h-5 text-primary-500" />
              <h3 className="text-lg font-bold text-primary-700 dark:text-primary-300">
                Filtros de Búsqueda
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cliente
                </label>
                <select
                  className="input-field"
                  value={filters.clientId}
                  onChange={(e) => handleFilterChange('clientId', e.target.value)}
                >
                  <option value="">Todos los clientes</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.rfc} - {client.razonSocial}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Estado
                </label>
                <select
                  className="input-field"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">Todos los estados</option>
                  <option value="Pending">Pendiente</option>
                  <option value="Submitted">Enviado</option>
                  <option value="InProgress">En Progreso</option>
                  <option value="Completed">Completado</option>
                  <option value="Failed">Fallido</option>
                  <option value="Error">Error</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha Desde
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={filters.fechaDesde}
                  onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha Hasta
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={filters.fechaHasta}
                  onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                className="btn-ghost"
              >
                <X className="w-4 h-4 mr-2" />
                Limpiar Filtros
              </button>
              <button
                onClick={() => loadDownloads()}
                className="btn-primary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </button>
            </div>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="alert alert-error mb-6">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              onClick={() => setError('')}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {clientsError && (
          <div className="alert alert-warning mb-6">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1">
              <div className="mb-2">{clientsError}</div>
              <button
                onClick={reloadClients}
                className="btn-ghost text-xs"
              >
                Reintentar carga de clientes
              </button>
            </div>
            <button
              onClick={() => setClientsError('')}
              className="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {success && (
          <div className="alert alert-success mb-6">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{success}</span>
            <button
              onClick={() => setSuccess('')}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Estadísticas e Información sobre Estados */}
        <div
          className={`card mb-6 transition-all duration-300 ease-in-out ${showStatsInfo ? 'opacity-100 max-h-screen' : 'opacity-0 max-h-0 overflow-hidden'
            }`}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <BarChart3 className="w-6 h-6 text-primary-500" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                Estadísticas y Estados
              </h3>
            </div>
            <button
              onClick={() => {
                isScrolling.current = true;
                setShowStatsInfo(!showStatsInfo);
                setTimeout(() => {
                  isScrolling.current = false;
                }, 400);
              }}
              className="btn-ghost text-sm"
            >
              {showStatsInfo ? (
                <>
                  <EyeOff className="w-4 h-4 mr-1" />
                  Ocultar
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-1" />
                  Mostrar
                </>
              )}
            </button>
          </div>

          {/* Estadísticas */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 sm:mb-0">
                📈 Resumen General
              </h4>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <div>Mostrando {downloads.length} de {pagination.totalItems} resultados
                  {hasActiveFilters && <span className="text-yellow-600 dark:text-yellow-400 font-bold"> (filtrado)</span>}</div>
                <div className="text-xs text-gray-500 dark:text-gray-500">
                  🔄 Última actualización: {lastUpdate.toLocaleTimeString('es-ES')}
                  <small> (Auto-refresh cada minuto)</small>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card-hover p-4 text-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Users className="w-4 h-4 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-blue-600 dark:text-blue-400">{clients.length}</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Clientes disponibles</p>
              </div>

              <div className="card-hover p-4 text-center bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <Download className="w-4 h-4 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-green-600 dark:text-green-400">{pagination.totalItems}</h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Total de descargas</p>
              </div>

              <div className="card-hover p-4 text-center bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20">
                <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <RefreshCw className="w-4 h-4 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {downloads.filter(d => ['IN_PROGRESS', 'SUBMITTED', 'InProgress', 'Submitted'].includes(d.status)).length}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">En proceso</p>
              </div>

              <div className="card-hover p-4 text-center bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20">
                <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <XCircle className="w-4 h-4 text-white" />
                </div>
                <h4 className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {downloads.filter(d => ['FAILED', 'Failed', 'ERROR', 'Error'].includes(d.status)).length}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">Con errores</p>
              </div>
            </div>
          </div>

          {/* Información de Estados */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              📋 Información sobre Estados
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {[
                { status: 'Pendiente', progress: '10%', desc: 'Solicitud creada, no enviada al SAT', class: 'status-pending' },
                { status: 'Enviado', progress: '25%', desc: 'Enviada al SAT, verificando periódicamente', class: 'status-pending' },
                { status: 'En Progreso', progress: '75%', desc: 'Lista para descarga, descargando paquetes', class: 'status-processing' },
                { status: 'Completado', progress: '100%', desc: 'Descarga completada y XMLs procesados', class: 'status-active' },
                { status: 'Fallido', progress: '0%', desc: 'Error en cualquier paso del proceso', class: 'status-inactive' }
              ].map((item, index) => (
                <div key={index}>
                  <span className={`${item.class} text-xs`}>
                    {item.status} ({item.progress})
                  </span>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Encabezado de la tabla con botones de acción */}
        <div className="card mb-0 rounded-b-none border-b-0">
          <div className="flex flex-wrap gap-3 justify-end p-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary relative ${showFilters ? 'bg-primary-500 text-white' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
              {hasActiveFilters && (
                <span className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {Object.values(filters).filter(v => v).length}
                </span>
              )}
            </button>
            <button
              onClick={reloadClients}
              disabled={clientsLoading}
              className="btn-secondary"
            >
              {clientsLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Recargar Clientes
            </button>
            <button
              onClick={handleCreateDownload}
              disabled={clientsLoading}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Descarga
            </button>
          </div>
        </div>

        <div className="card rounded-t-none border-t-0 pt-0">
          {downloads.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {hasActiveFilters ? 'No se encontraron descargas con los filtros aplicados' : 'No hay descargas registradas'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {hasActiveFilters
                  ? 'Intenta ajustar los filtros o crear una nueva descarga'
                  : 'Comienza creando tu primera descarga masiva del SAT'
                }
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <button
                  onClick={handleCreateDownload}
                  disabled={clientsLoading || clients.length === 0}
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {clients.length === 0 ? 'Necesitas clientes primero' : 'Crear primera descarga'}
                </button>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="btn-secondary"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Limpiar Filtros
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="table-container max-h-96">
                <table className="table">
                  <thead className="table-header sticky top-0 z-10 shadow-sm">
                    <tr>
                      {[
                        { key: 'ClienteRfc', label: 'Cliente' },
                        { key: 'FechaInicio', label: 'Período' },
                        { key: 'Status', label: 'Estado' },
                        { key: null, label: 'Progreso' },
                        { key: 'TotalFacturas', label: 'Facturas' },
                        { key: 'CreatedAt', label: 'Fecha Creación' },
                        { key: null, label: 'Acciones' }
                      ].map((column, index) => (
                        <th
                          key={index}
                          className={`table-header-cell ${column.key ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700' : ''}`}
                          onClick={column.key ? () => handleSortChange(column.key) : undefined}
                        >
                          <div className="flex items-center space-x-2">
                            <span>{column.label}</span>
                            {column.key && getSortIcon(column.key)}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {downloads.map((download, index) => (
                      <tr key={download.id} className="table-row">
                        <td className="table-cell min-w-48">
                          <div className="font-semibold text-primary-600 dark:text-primary-400">
                            {download.clienteRfc || getClientName(download.clientId)}
                          </div>
                        </td>
                        <td className="table-cell min-w-36">
                          <div className="flex items-center space-x-1 text-sm">
                            <Calendar className="w-3 h-3 text-gray-400" />
                            <span>{formatDateRange(download.fechaInicio, download.fechaFin)}</span>
                          </div>
                        </td>
                        <td className="table-cell">
                          {getStatusBadge(download.status)}
                        </td>
                        <td className="table-cell min-w-32">
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-1">
                            <div
                              className={`h-2 rounded-full transition-all duration-500 ${getProgressPercentage(download.status) === 100 ? 'bg-green-500' :
                                  getProgressPercentage(download.status) >= 50 ? 'bg-blue-500' :
                                    getProgressPercentage(download.status) > 0 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                              style={{ width: `${getProgressPercentage(download.status)}%` }}
                            ></div>
                          </div>
                          <small className="text-gray-500 dark:text-gray-400 text-xs">
                            {getProgressPercentage(download.status)}%
                          </small>
                        </td>
                        <td className="table-cell text-center">
                          <div className="font-medium">
                            {download.totalFacturas || 0}
                          </div>
                        </td>
                        <td className="table-cell min-w-36">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-1 text-sm">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span>{formatDate(download.createdAt)}</span>
                            </div>
                            {download.completedAt && ['COMPLETED', 'Completed'].includes(download.status) && (
                              <div className="flex items-center space-x-1 text-xs text-green-600 dark:text-green-400">
                                <CheckCircle className="w-3 h-3" />
                                <span>Completado: {formatDate(download.completedAt)}</span>
                              </div>
                            )}
                            {['FAILED', 'Failed', 'ERROR', 'Error'].includes(download.status) && (
                              <div className="flex items-center space-x-1 text-xs text-red-600 dark:text-red-400">
                                <XCircle className="w-3 h-3" />
                                <span>Error: {download.failedAt ? formatDate(download.failedAt) : 'Proceso fallido'}</span>
                              </div>
                            )}
                            {download.errorMessage && ['FAILED', 'Failed', 'ERROR', 'Error'].includes(download.status) && (
                              <div
                                className="text-xs bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-2 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                title={formatErrorMessage(download.errorMessage)}
                              >
                                <div className="text-red-700 dark:text-red-400 line-clamp-2">
                                  {formatErrorMessage(download.errorMessage).length > 60
                                    ? formatErrorMessage(download.errorMessage).substring(0, 60) + '...'
                                    : formatErrorMessage(download.errorMessage)}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="table-cell min-w-36">
                          <div className="flex flex-wrap gap-2">
                            {['COMPLETED', 'Completed'].includes(download.status) && (
                              <>
                                <button
                                  onClick={() => handleDownloadPackage(download.id)}
                                  className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                                  title="Descargar archivo ZIP"
                                >
                                  <Package className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => window.open(`/invoices?downloadId=${download.id}`, '_blank')}
                                  className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                  title="Ver facturas procesadas"
                                >
                                  <FileText className="w-4 h-4" />
                                </button>
                              </>
                            )}

                            {['PENDING', 'SUBMITTED', 'IN_PROGRESS', 'Pending', 'Submitted', 'InProgress'].includes(download.status) && (
                              <button
                                onClick={() => handleCancelDownload(download.id)}
                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Cancelar descarga"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación */}
              {pagination.totalPages > 1 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 gap-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Página {pagination.currentPage} de {pagination.totalPages}
                    ({pagination.totalItems} registros total)
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={!pagination.hasPreviousPage}
                      className="btn-ghost text-xs"
                    >
                      ⏮️ Primera
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={!pagination.hasPreviousPage}
                      className="btn-ghost text-xs"
                    >
                      ⬅️ Anterior
                    </button>

                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      const startPage = Math.max(1, pagination.currentPage - 2);
                      const pageNumber = startPage + i;
                      if (pageNumber <= pagination.totalPages) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`text-xs min-w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${pageNumber === pagination.currentPage
                                ? 'bg-primary-500 text-white'
                                : 'btn-ghost'
                              }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      }
                      return null;
                    })}

                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={!pagination.hasNextPage}
                      className="btn-ghost text-xs"
                    >
                      Siguiente ➡️
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.totalPages)}
                      disabled={!pagination.hasNextPage}
                      className="btn-ghost text-xs"
                    >
                      Última ⏭️
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showModal && (
        <DownloadModal
          clients={clients}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default DownloadManagement;